/**
 * Ultra-light dictionary for the embed widget.
 *
 * The widget has only a handful of visible strings and is bundled as a tiny
 * Vite/Preact-style artifact for Cloudflare Pages — we intentionally skip
 * heavy i18n libs here.
 *
 * The locale arrives either from the host page (via `HostBridge` settings)
 * or is auto-detected from `navigator.language`. Falls back to `pt-BR`.
 */

export type WidgetLocale = "en" | "pt-BR";

export const DEFAULT_WIDGET_LOCALE: WidgetLocale = "pt-BR";

const DICT = {
  "pt-BR": {
    typeMessage: "Digite sua mensagem...",
    send: "Enviar",
    sendAttachment: "Enviar anexo",
    loading: "Carregando…",
    dropFile: "Solte o arquivo para enviar",
    connecting: "Conectando...",
    disconnected: "Desconectado",
    offlineHint: "Você parece estar offline.",
    minimize: "Minimizar",
    close: "Fechar",
    today: "Hoje",
    yesterday: "Ontem",
    you: "Você",
    agent: "Atendente",
    newMessage: "Nova mensagem",
    poweredBy: "Powered by",
    sessionError: "Não foi possível iniciar a conversa. Tente novamente.",
    missingSiteKey: "Widget sem siteKey. A instalação está incompleta.",
    startSessionFailed: "Falha ao iniciar a sessão",
  },
  en: {
    typeMessage: "Type your message...",
    send: "Send",
    sendAttachment: "Send attachment",
    loading: "Loading…",
    dropFile: "Drop the file to send",
    connecting: "Connecting...",
    disconnected: "Disconnected",
    offlineHint: "You appear to be offline.",
    minimize: "Minimize",
    close: "Close",
    today: "Today",
    yesterday: "Yesterday",
    you: "You",
    agent: "Agent",
    newMessage: "New message",
    poweredBy: "Powered by",
    sessionError: "Couldn't start the conversation. Please try again.",
    missingSiteKey: "Widget missing siteKey. Install is incomplete.",
    startSessionFailed: "Failed to start session",
  },
} as const;

export type WidgetTranslationKey = keyof (typeof DICT)["pt-BR"];

/**
 * Resolve a known locale tag into the widget's supported set.
 */
export function normalizeWidgetLocale(
  input?: string | null,
): WidgetLocale {
  if (!input) return DEFAULT_WIDGET_LOCALE;
  const lower = input.toLowerCase();
  if (lower === "en" || lower.startsWith("en-")) return "en";
  if (lower === "pt-br" || lower.startsWith("pt-")) return "pt-BR";
  return DEFAULT_WIDGET_LOCALE;
}

/**
 * Translate a key to the given locale, with fallback to the default.
 */
export function t(key: WidgetTranslationKey, locale: WidgetLocale): string {
  return DICT[locale]?.[key] ?? DICT[DEFAULT_WIDGET_LOCALE][key];
}

/**
 * Best-effort auto-detect from the current browser, for hosts that don't
 * pass a locale explicitly.
 */
export function detectWidgetLocale(): WidgetLocale {
  if (typeof navigator === "undefined") return DEFAULT_WIDGET_LOCALE;
  return normalizeWidgetLocale(navigator.language);
}
